<template>
  <va-form
    :id="isEdit ? popup.id : ''"
    class="create-popup__form"
    ref="form"
    :item="popup"
    v-model="form"
    :before-save="beforeSave"
    :after-save="afterSave"
    :modify-before-send="modifyBeforeSend"
    :custom-redirect-route="
      !!this.$route.query.domainId
        ? {
            name: 'domains_show',
            params: { id: domainId },
            query: { tab: 'pages_add_ons' },
          }
        : {
            name: 'modals_list',
          }
    "
    :required-fields-set="requiredFieldsSet">
    <v-dialog :content-class="'popup-preview__modal'" v-model="previewPopupOpen">
      <div class="popup-preview__modal-close-btn" @click="previewPopupOpen = false">
        <v-icon>mdi-close-circle</v-icon>
      </div>
      <img :src="popupPreviewImg" />
    </v-dialog>
    <!-- Details -->
    <v-card class="spacing-bottom--16">
      <div class="d-flex">
        <div class="d-flex flex-column">
          <v-card-title>{{ $t("resources.popups.popup_details_block_title") }}</v-card-title>
          <v-card-subtitle class="spacing-bottom--16">{{
            $t("resources.popups.popup_details_block_subtitle")
          }}</v-card-subtitle>
        </div>
        <v-card-actions class="ml-auto">
          <div @click="previewPopup" class="d-flex align-center popup__preview-wrapper">
            <v-icon size="19" class="info-tooltip" color="secondary-btn">mdi-information</v-icon>
            <span class="popup__preview-text">{{ $t("resources.popups.popup_preview") }}</span>
          </div>
        </v-card-actions>
      </div>
      <va-text-input
        v-model="form.name"
        required
        source="name"
        class="spacing-bottom--16"
        :label="$t('resources.popups.popup_name')"
        :placeholder="$t('resources.popups.popup_name_placeholder')"></va-text-input>
      <va-autocomplete-input
        v-model="form.domain"
        source="domain"
        :disabled="isDomainProvided"
        class="spacing-bottom--16"
        required
        :placeholder="$t('resources.popups.popup_domain_placeholder')"
        :label="$t('resources.popups.popup_domain')"
        :choices="domainsList"
        :min-chars="1"
        cache-items
        @change="domainChanged"></va-autocomplete-input>

      <div class="page-wrapper" v-if="form.pages && form.pages.length > 0">
        <div class="page-label">
          {{ $t("resources.popups.fields.page") }}
        </div>
        <div class="chip-container">
          <v-chip
            color="primary-20"
            text-color="secondary-btn"
            close
            close-icon="mdi-close"
            @click:close="removePage(page)"
            v-for="page in getPageArray"
            :key="page.id">
            {{ getPageName(page) }}
          </v-chip>
          <div v-if="form.pages.length > 12">
            <v-btn
              v-if="!showAllPageChips"
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = true"
              :ripple="false">
              {{ $t("resources.calendars.show_all") }}
            </v-btn>
            <v-btn
              v-else
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = false"
              :ripple="false">
              {{ $t("resources.calendars.collapse") }}
            </v-btn>
          </div>
        </div>
      </div>

      <v-switch
        class="spacing-bottom--16"
        v-model="form.showPreHeader"
        inset
        color="primary"
        :hide-details="'auto'"
        :ripple="false"
        :label="$t('resources.popups.popup_show_pre_heading')"></v-switch>

      <va-text-input
        v-if="form.showPreHeader"
        v-model="form.preHeader"
        source="preHeader"
        class="spacing-bottom--16"
        :label="$t('resources.popups.popup_pre_header')"
        :placeholder="$t('resources.popups.popup_pre_header_placeholder')"></va-text-input>

      <va-text-input
        v-model="form.header"
        source="header"
        class="spacing-bottom--16"
        :label="$t('resources.popups.popup_header')"
        :placeholder="$t('resources.popups.popup_header_placeholder')"></va-text-input>

      <va-select-input
        class="spacing-bottom--16"
        :source="'show'"
        v-model="form.show"
        multiple
        :choices="MODAL_DISPLAY_TYPES"
        :label="$t('resources.popups.popup_display_type')"
        :placeholder="$t('resources.popups.popup_display_type_placeholder')"></va-select-input>

      <va-tinymce-input
        source="content"
        :title="$t('resources.popups.popup_content')"
        :current-content="form.content"
        :editor-height="605"
        @update="updatePopupContent" />
    </v-card>

    <required-field-text></required-field-text>
  </va-form>
</template>

<script>
  // Mixins
  import crudFormMixin from "@/mixins/crudFormMixin";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import providedDomain from "@/mixins/providedDomain";
  // Constants
  import { MODAL_DISPLAY_TYPES } from "@/constant";
  // Utils
  import { parseToHtml } from "@/utility/parseToHtml";
  import { cleanupTinymceContent } from "@/utility/cleanupTinymceContent";
  import { serializeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
  import { truncateText } from "@/utility/truncateText";
  // Images
  import popupPreviewImg from "@/assets/images/popup_preview/popup.png";
  //Component
  import RequiredFieldText from "@/components/custom/RequiredFieldText";

  export default {
    name: "PopupForm",
    components: {
      RequiredFieldText,
    },
    mixins: [ConnectedDomainsList, crudFormMixin, providedDomain],
    props: {
      popup: {
        type: Object,
        default: () => {},
      },
      // Determines if form is used while editing
      isEdit: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        MODAL_DISPLAY_TYPES,
        previewPopupOpen: false,
        popupPreviewImg,
        requiredFormFields: ["name", "domain"],
        form: {},
        oldDomain: "",
        oldPages: [],
        showAllPageChips: false,
      };
    },
    computed: {
      domainId() {
        return this.form.domain?.replace("/api/domains/", "");
      },
      resourcesFilter() {
        return this.form.domain ? { domain: this.form.domain.replace("/api/domains/", "") } : null;
      },
      getPageArray() {
        if (this.showAllPageChips) {
          return this.form.pages;
        } else {
          return this.form.pages.slice(0, 12);
        }
      },
    },
    beforeMount() {
      this.form = this.popup ? this.popup : {};
      if (!this.isEdit) {
        delete this.form["@context"];
        delete this.form["@id"];
        delete this.form["@type"];
      }
      this.form.domain = this.popup?.domain?.["@id"];
      this.form.show = this.popup?.show;
      this.form.showPreHeader = this.popup?.showPreHeader;
      this.form.template = this.popup?.template === "main" || !this.popup?.template ? "default" : this.popup?.template;
      this.form.pages = this.popup?.pages || [];
      this.oldDomain = this.popup?.domain;
      this.oldPages = this.popup?.pages || [];

      this.updatePopupContent(this.popup?.content);
    },
    methods: {
      getPageName(page) {
        return truncateText(page.headline);
      },
      removePage(page) {
        this.form.pages = this.form.pages.filter(item => item["@id"] !== page["@id"]);
        this.oldPages = this.form.pages;
      },
      modifyBeforeSend(data) {
        data.pages = data.pages.map(page => page["@id"]);

        if (data.content) {
          const content = parseToHtml(data.content);
          serializeDynamicComponents(content);
          data.content = content.body.innerHTML;
        }

        return data;
      },
      domainChanged(domain) {
        if (this.oldDomain !== domain) {
          this.form.pages = [];
        } else {
          this.form.pages = this.oldPages;
        }
      },
      previewPopup() {
        this.previewPopupOpen = true;
      },
      updatePopupContent(newValue) {
        const content = newValue ? newValue : "";
        let htmlElementFromContent = parseToHtml(content);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        cleanupTinymceContent(htmlElementFromContent);
        this.form.content = htmlElementFromContent.body.innerHTML;
      },
      submit() {
        this.$refs.form.onSubmit();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .popup__preview-wrapper {
    cursor: pointer;
    .v-icon {
      line-height: 24px;
    }
    .popup__preview-text {
      margin-left: 5px;
      @include text-button-small();
      color: get-color("secondary-btn");
    }
  }

  .page-label {
    @include text-caption-bold();
    color: get-color("subtitle");
    margin-bottom: 12px;
  }

  .page-wrapper {
    margin-bottom: 12px;
  }

  .chip-container {
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: 6px;
    align-items: center;

    .chip-action-button.v-btn {
      padding: 5px 10px !important;
      height: unset !important;
      min-width: unset;
      color: get-color("secondary-btn") !important;
    }
  }
</style>
